import { createStyles, Typography, withStyles, WithStyles } from '@material-ui/core';
import React, { useState } from 'react';
import MomentUtils from '@date-io/moment';
import { MuiPickersUtilsProvider, TimePicker } from '@material-ui/pickers';
import moment, { Moment } from 'moment';

import { colors } from 'src/themes/colors';
import { AvailableHourRange } from 'src/models/Sessions';

const styles = () =>
  createStyles({
    hour: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    hourInput: {
      flex: 0,
      minWidth: 80,
      '& .MuiInput-input': {
        padding: 6,
      },
    },
    hourButton: {
      backgroundColor: colors.blue7,
      fontSize: 20,
      fontWeight: 600,
      padding: '0px 8px',
      margin: '5px 0px 0px 5px',
      maxWidth: 28,
      cursor: 'pointer',
    },
    addHours: {
      color: colors.blue6,
    },
    deleteHourRange: {
      color: colors.red1,
    },
    durationSeparator: {
      fontSize: 26,
      color: colors.black1,
      marginLeft: 6,
      marginRight: 10,
    },
    error: {
      '& .MuiInputBase-root': {
        color: colors.red2,
      },
    },
  });

enum AvailableHourRangeInputTestID {
  StartTimeInput = 'AvailableHourRangeInputTestID.StartTimeInput',
  EndTimeInput = 'AvailableHourRangeInputTestID.EndTimeInput',
  AddHourRange = 'AvailableHourRangeInputTestID.AddHourRange',
  RemoveHourRange = 'AvailableHourRangeInputTestID.RemoveHourRange',
}

interface Props extends WithStyles<typeof styles> {
  addNewHourRange: () => void;
  deleteHourRange: () => void;
  onRangeUpdated: (start: string, end: string) => void;
  hour: AvailableHourRange;
  showAddButton: boolean;
  hasError?: boolean;
  testIDPostfix?: string;
}

const AvailableHoursInput: React.FC<Props> = ({
  hour,
  addNewHourRange,
  deleteHourRange,
  onRangeUpdated,
  showAddButton,
  hasError = false,
  classes,
  testIDPostfix = '',
}) => {
  const [startTime, setStartTime] = useState<Moment | null>(
    hour.start ? moment(hour.start, [moment.ISO_8601, 'HH:mm']) : null,
  );
  const [endTime, setEndTime] = useState<Moment | null>(
    hour.end ? moment(hour.end, [moment.ISO_8601, 'HH:mm']) : null,
  );

  const updateStartTime = (newStartTime: Moment | null) => {
    if (newStartTime) {
      setStartTime(newStartTime);

      if (endTime) {
        onRangeUpdated(newStartTime.format('HH:mm'), endTime.format('HH:mm'));
      }
    }
  };

  const updateEndTime = (newEndTime: Moment | null) => {
    if (newEndTime) {
      setEndTime(newEndTime);

      if (startTime) {
        onRangeUpdated(startTime.format('HH:mm'), newEndTime.format('HH:mm'));
      }
    }
  };

  const clearAndDeleteHours = () => {
    setStartTime(null);
    setEndTime(null);
    deleteHourRange();
  };

  return (
    <div className={classes.hour}>
      <MuiPickersUtilsProvider utils={MomentUtils}>
        <>
          <TimePicker
            clearable={false}
            ampm
            value={startTime}
            /* `onChange` doesn't work as you would expect, but is required */
            onChange={() => {}}
            onAccept={updateStartTime}
            format="hh:mm a"
            minutesStep={15}
            initialFocusedDate={moment('09:00 am', [moment.ISO_8601, 'HH:mm a'])}
            className={!hasError ? classes.hourInput : `${classes.hourInput} ${classes.error}`}
            data-testid={`${AvailableHourRangeInputTestID.StartTimeInput}${testIDPostfix}`}
          />

          <Typography className={classes.durationSeparator}>-</Typography>

          <TimePicker
            clearable={false}
            ampm
            value={endTime}
            /* `onChange` doesn't work as you would expect, but is required */
            onChange={() => {}}
            onAccept={updateEndTime}
            minutesStep={15}
            format="hh:mm a"
            initialFocusedDate={moment('09:00 pm', [moment.ISO_8601, 'HH:mm a'])}
            className={!hasError ? classes.hourInput : `${classes.hourInput} ${classes.error}`}
            data-testid={`${AvailableHourRangeInputTestID.EndTimeInput}${testIDPostfix}`}
          />
        </>
      </MuiPickersUtilsProvider>

      {startTime && endTime && (
        <div
          className={`${classes.hourButton} ${classes.addHours}`}
          onClick={addNewHourRange}
          data-testid={AvailableHourRangeInputTestID.AddHourRange}
        >
          +
        </div>
      )}

      {/* Part after `||` adds the delete button if they click `+` multiple times */}
      {(startTime || endTime) && (
        <div
          className={`${classes.hourButton} ${classes.deleteHourRange}`}
          onClick={clearAndDeleteHours}
          data-testid={AvailableHourRangeInputTestID.RemoveHourRange}
        >
          &times;
        </div>
      )}
    </div>
  );
};

export default withStyles(styles)(AvailableHoursInput);
